// Paper EOBs fixtures (EOB_HANDOFF §9). Copy is from the boards.
import type { CheckClaim, EobCheck, EobDoc, FaxSetup } from './types';
import { PMS } from '@/features/shared/pms';

export const FAX_SETUP: FaxSetup = {
  address: 'eobs.alamoheights@fax.elva.ai',
  senders: 'faxes@ringcentral.com',
  setUpBy: 'Ava Thompson (Lone Star Billing)',
  setUpWhen: 'Oct 1',
};

function readyClaim(id: string, patient: string, visit: string, procedures: string, paid: number, fee: number, allowed: number): CheckClaim {
  return {
    id,
    patient,
    visit,
    procedures,
    paid,
    status: 'ready',
    kind: 'normal',
    headline: 'Nothing unusual',
    pageCrop: `PATIENT: ${patient.toUpperCase()}\n${procedures}   SUBMITTED ${fee.toFixed(2)}   ALLOWED ${allowed.toFixed(2)}   PAID ${paid.toFixed(2)}`,
    pageNumber: 1,
    pageAria: `Scan of ${patient}'s line on the EOB, paid $${paid.toFixed(2)}`,
    ticks: [{ text: `Found the claim: ${patient}, ${visit}.` }, { text: 'Allowed amounts match your fee schedule.' }],
    breakdown: [
      { label: 'Delta paid', amount: paid },
      { label: 'Write-off', detail: `your fee $${fee.toFixed(2)} − $${allowed.toFixed(2)} allowed`, amount: Math.round((fee - allowed) * 100) / 100 },
      { label: `${patient.split(' ')[0] ?? patient} owes`, amount: Math.round((allowed - paid) * 100) / 100, kind: 'total' },
    ],
  };
}

const ISAAC: CheckClaim = {
  id: 'cl-isaac',
  patient: 'Isaac Wright',
  visit: 'Oct 2',
  procedures: 'D3330 #30',
  paid: 854,
  status: 'ready',
  statusNote: 'Smudged; ELVA checked it',
  kind: 'smudged',
  headline: 'Isaac Wright, Oct 2: the paid amount is smudged on the fax',
  pageCrop: 'PATIENT: WRIGHT, ISAAC   ID: 70334128\nCLAIM #: 26275D0118\nDOS       PROC  TH  SUBMITTED  ALLOWED   PAID\n10/02/26  D3330 30   1,410.00   1,220.00  854.00',
  pageNumber: 2,
  pageAria: 'Scan of Isaac Wright’s line on page 2 of the fax. The paid amount is smudged; it reads 854.00.',
  ticks: [
    { text: 'The check is $3,486.20. The other 8 claims add up to $2,632.20, which leaves $854.00.' },
    { text: 'Delta allows $1,220.00 for D3330. Isaac’s plan pays 70%, which is $854.00.' },
    { text: 'Delta’s portal shows $854.00 paid on this claim.' },
  ],
  breakdown: [
    { label: 'Delta paid', amount: 854 },
    { label: 'Write-off', detail: 'your fee $1,410.00 − $1,220.00 allowed', amount: 190 },
    { label: 'Isaac owes', detail: 'printed on the EOB as patient responsibility', amount: 366, kind: 'total' },
  ],
  smudgeValues: [
    { value: 854, source: 'the check total minus the other claims' },
    { value: 584, source: 'the allowed amount × the plan’s percentage' },
    { value: 854, source: 'Delta’s portal' },
  ],
};

const CARLOS: CheckClaim = {
  id: 'cl-carlos',
  patient: 'Carlos Mendes',
  visit: 'Oct 5',
  procedures: 'D7140',
  paid: 126,
  status: 'needs_you',
  statusNote: `No claim in ${PMS}`,
  kind: 'no_claim',
  headline: `Carlos Mendes: the visit is complete in ${PMS}, but no claim was created`,
  pageCrop: 'PATIENT: MENDES, CARLOS\n10/05/26  D7140   SUBMITTED 180.00   ALLOWED 126.00   PAID 126.00',
  pageNumber: 3,
  pageAria: 'Scan of Carlos Mendes’s line, paid $126.00',
  breakdown: [{ label: 'Delta paid', amount: 126 }, { label: 'Carlos owes', amount: 0, kind: 'total' }],
  question: {
    text: `There’s no claim in ${PMS} for this visit`,
    options: [
      { id: 'create', label: `Create the claim in ${PMS}, then post`, effect: 'enable_post' },
      { id: 'different', label: 'It’s a different visit', effect: 'search' },
      { id: 'not_ours', label: 'Not our patient', effect: 'not_our_patient' },
    ],
    meta: `ELVA creates the claim for Oct 5 as already sent, so ${PMS} doesn’t send it again, then posts $126.00.`,
  },
};

export function buildDeltaCheck(): EobCheck {
  return {
    id: 'CHK-DELTA',
    payer: 'Delta Dental of Texas',
    checkNumber: '004417820',
    amount: 3486.2,
    checkDate: 'Oct 9, 2026',
    source: 'Paper EOB · faxed today at 9:12 AM · 4 pages · check dated Oct 9, 2026',
    group: 'needs_you',
    listSecond: 'Check 004417820 · paper EOB',
    listReason: '9 claims, 1 needs you',
    listMeta: 'Faxed today 9:12 AM',
    addsUp: { ok: true, text: 'The 9 lines add up to the check: $3,486.20' },
    claims: [
      ISAAC,
      CARLOS,
      readyClaim('cl-olivia', 'Olivia Turner', 'Sep 28', 'D0120, D1110', 164, 236, 164),
      readyClaim('cl-mateo', 'Mateo Garcia', 'Sep 29', 'D2391 #19', 118.4, 180, 118.4),
      readyClaim('cl-leah', 'Leah Morgan', 'Sep 30', 'D2750 #14', 612, 1180, 612),
      readyClaim('cl-samuel', 'Samuel Lee', 'Oct 1', 'D4341 ×2', 286.8, 480, 286.8),
      readyClaim('cl-zoe', 'Zoe Kaplan', 'Oct 1', 'D0150, D0210', 212, 350, 212),
      readyClaim('cl-emma', 'Emma Silva', 'Oct 2', 'D1110', 98, 140, 98),
      readyClaim('cl-ruby', 'Ruby Allen', 'Oct 6', 'D2740, D2950 #19', 1015, 1580, 1015),
    ],
  };
}

export function buildGuardianCheck(): EobCheck {
  return {
    id: 'CHK-GUARDIAN',
    payer: 'Guardian',
    checkNumber: '77120458',
    amount: 1284.5,
    checkDate: 'Oct 8, 2026',
    source: 'Paper EOB · faxed today at 8:40 AM · pages 1, 2 and 4 of 4 · check dated Oct 8, 2026',
    group: 'ready',
    listSecond: 'Check 77120458 · paper EOB',
    listReason: '4 claims · ELVA got a missing page from the portal',
    listMeta: 'Faxed today 8:40 AM',
    addsUp: { ok: true, text: 'The 4 lines add up to the check: $1,284.50' },
    handled: {
      ticks: [
        { text: '8:52 AM · signed in to Guardian’s provider portal and downloaded the EOB for check 77120458', links: [{ label: 'EOB from the portal' }, { label: 'See the portal visit in ELVA’s log' }] },
        { text: 'Pages 1, 2 and 4 from the portal match the fax line for line.' },
        { text: 'Page 3 held Nina Hale, Sep 25, $316.40.' },
      ],
      note: 'Nothing for you to decide here.',
    },
    claims: [
      readyClaim('cl-nina', 'Nina Hale', 'Sep 25', 'D2740 #8', 316.4, 520, 316.4),
      readyClaim('cl-g2', 'Priya Varma', 'Sep 26', 'D1110, D0120', 162, 236, 162),
      readyClaim('cl-g3', 'Owen Fry', 'Sep 27', 'D2392 #30', 290, 420, 290),
      readyClaim('cl-g4', 'Ada Boone', 'Sep 29', 'D4910', 516.1, 720, 516.1),
    ],
  };
}

export function buildDocuments(): EobDoc[] {
  return [
    { id: 'doc-1', received: 'Today 9:12 AM', how: 'Fax · 4 pages', found: ['Delta Dental of Texas · check 004417820 · $3,486.20 · 9 claims'], badge: { label: 'Needs you', tone: 'warn' }, meta: '1 of 9 claims needs you', actions: [{ label: 'Open', kind: 'open' }] },
    { id: 'doc-2', received: 'Today 8:40 AM', how: 'Fax · 3 pages', found: ['Guardian · check 77120458 · $1,284.50 · 4 claims'], badge: { label: 'In To post', tone: 'info' }, meta: 'Page 3 was missing; ELVA got it from Guardian’s portal', actions: [{ label: 'Open', kind: 'open' }] },
    { id: 'doc-3', received: 'Yesterday 4:05 PM', how: 'Scan · 12 pages, uploaded by Ava Thompson', found: ['MetLife · check 9031447 · $842.60 · 4 claims', 'Cigna · check 66104822 · $1,096.00 · 5 claims', 'United Concordia · check 2208815 · $410.00 · 2 claims'], badge: { label: 'Posted', tone: 'ok' }, meta: 'All 3 posted, 11 claims', actions: [{ label: 'See in Posted', kind: 'see_posted' }] },
    { id: 'doc-4', received: 'Yesterday 2:31 PM', how: 'Fax · 2 pages', found: ['Not an EOB: a referral letter from Dr. Kaye'], badge: { label: 'Skipped', tone: 'mute' }, meta: '', actions: [{ label: 'View', kind: 'view' }, { label: 'This is an EOB', kind: 'is_eob' }] },
    { id: 'doc-5', received: 'Oct 13, 11:02 AM', how: 'Fax · 4 pages', found: ['Aetna · check 51200987 · $2,210.00'], badge: { label: 'Skipped', tone: 'mute' }, meta: 'The same EOB was faxed on Oct 12', actions: [{ label: 'View', kind: 'view' }] },
    { id: 'doc-6', received: 'Oct 12, 3:18 PM', how: 'Fax · 2 pages', found: ['Cigna · virtual card payment · $318.40'], badge: { label: 'Kept for later', tone: 'mute' }, meta: 'For card payments, coming next. Card number hidden.', actions: [] },
    { id: 'doc-7', received: 'Oct 12, 10:47 AM', how: 'Fax · 4 pages', found: ['Aetna · check 51200987 · $2,210.00 · 7 claims'], badge: { label: 'Posted', tone: 'ok' }, meta: '7 claims posted', actions: [{ label: 'See in Posted', kind: 'see_posted' }] },
    { id: 'doc-8', received: 'Oct 9, 1:20 PM', how: 'Phone photo · 1 page, uploaded by Ryan Cole', found: ['Too dark to read'], badge: { label: 'Skipped', tone: 'mute' }, meta: 'Ryan was asked to retake it', actions: [] },
  ];
}
